import { html, nothing } from 'lit';

import { customElement } from 'lit/decorators.js';
import { t } from 'src/i18n';
import 'src/content/entity-content';
import '../../library/editors/index.js';
import '../../library/editors/elements/d-select-checkbox.js';
import '../../library/fields/index.js';
import '../../library/fields/d-expansion.js';
import '../../library/fields/d-view-html.js';
import '../../library/lists/d-list-section-attachment.js';
import '../../pages/computers-page/d-edit-single-related-item.js';
import { DataMapElement } from './data-map-element.js';
import './d-view-checklist.js';
import './d-view-checkpoint.js';
import './d-view-related-items.js';
import type { LocalEditItem } from './local-edit-item';
import type { ComputerMapLocal } from './computer-map-local';
import type { SelectDropdownOption } from '../../library/editors/elements/select-dropdown-option';

/**
 *
 */
@customElement('d-computer-unit-local')
export class DComputerUnitLocal extends DataMapElement<ComputerMapLocal, LocalEditItem> {
  static readonly styles = [...DataMapElement.styles];
  printerPositionSecureOptions = [
    {
      value: 'true',
      text: t('computers.printerPositionSecure'),
    },
  ];

  private localUnitTypes: SelectDropdownOption[] = [
    { value: 'UNDEFINED', text: t('computers.options.chooseType') },
    { value: 'Server', text: t('computers.options.server') },
    { value: 'Arbeidsstasjon', text: t('computers.options.workstationClient') },
    { value: 'mobile', text: t('computers.options.mobileDevice') },
  ];

  _displayTypeName(): string {
    if (this.item.type === 'Server') {
      return t('computers.options.server');
    }
    if (this.item.type === 'Arbeidsstasjon') {
      return t('computers.options.workstationClient');
    }
    if (this.item.type === 'mobile') {
      return t('computers.options.mobileDevice');
    }
    return t('computers.localDevice');
  }

  _displayName() {
    return this.item.name;
  }

  _operatorTypes(networkConnection) {
    const list = [
      { value: 'UNDEFINED', text: t('computers.options.operatorChoose') },
      { value: 'NONE', text: t('computers.options.none') },
    ];
    if (networkConnection !== 'NONE' && networkConnection !== '') {
      list.push({ value: 'INHERIT', text: t('computers.options.operatorInheritNetwork') });
    }
    list.push(
      { value: 'internal', text: t('computers.options.operatorInternal') },
      { value: 'external', text: t('computers.options.operatorExternal') },
      {
        value: 'internalAndExternal',
        text: t('computers.options.operatorInternalAndExternal'),
      },
    );
    return list;
  }

  _getOperatorTypeValue(id) {
    let result = '';
    const list = this._operatorTypes(this.item.networkUuid);
    for (const item of list) {
      if (id === item.value) {
        result = item.text;
      }
    }

    if (result === t('computers.options.operatorChoose')) {
      result = t('computers.options.notSelected');
    }

    return result;
  }

  _isValid(): boolean {
    return true;
  }

  _operatorIsExternal(operatorType) {
    return operatorType === 'external' || operatorType === 'internalAndExternal';
  }
  renderUnitView() {
    return html`<section>
      <d-section>
        <d-view-text
          field="localUnits_systemUpdateOperatorType"
          value="${this._getOperatorTypeValue(this.item.systemUpdateOperatorType)}"
        >
        </d-view-text>
        <d-view-text
          field="localUnits_systemUpdateOperator"
          value="${this._getName(this.item.partners, this.item.systemUpdateOperator)}"
        >
        </d-view-text>
      </d-section>
      <d-section>
        <d-view-text
          field="localUnits_antiVirusOperatorType"
          value="${this._getOperatorTypeValue(this.item.antiVirusOperatorType)}"
        >
        </d-view-text>
        <d-view-text
          field="localUnits_antiVirusOperator"
          value="${this._getName(this.item.partners, this.item.antiVirusOperator)}"
        >
        </d-view-text>
      </d-section>

      ${!this.item.computerIsInNetworkWithMedicalData
        ? nothing
        : html`<d-section>
            <d-view-checklist
              .checked="${this.item.antiVirusRequirements}"
              entity="localUnits"
              field="localUnits_antiVirusRequirements"
              .options="${this.item.antiVirusRequirementsOptions}"
            >
            </d-view-checklist>
          </d-section>`}
      ${this.item.type === 'mobile'
        ? nothing
        : html`<d-section>
            <d-view-text field="localUnits_location" value="${this.item.location}"> </d-view-text>
          </d-section>`}
      ${!this.item.computerIsInNetworkWithMedicalData
        ? nothing
        : html`<d-section>
            <d-view-checklist
              .checked=${[]}
              entity="localUnits"
              field="localUnits_physicalSecurityRequirements"
              .options=${[]}
            >
            </d-view-checklist>
            ${!this.item.hasMedicalData
              ? nothing
              : html`<d-view-checkpoint
                  ?checked="${this.item.restrictedPhysicalAccess}"
                  field="localUnits_restrictedPhysicalAccess"
                >
                </d-view-checkpoint>`}
            ${!this.item.hasMedicalData
              ? nothing
              : html`<d-view-checkpoint ?checked="${this.item.elevated}" field="localUnits_elevated">
                </d-view-checkpoint>`}
            ${!this.item.hasMedicalData
              ? nothing
              : html`<d-view-checkpoint ?checked="${this.item.locked}" field="localUnits_locked"> </d-view-checkpoint>`}
            ${!this.item.computerIsInNetworkWithMedicalData
              ? nothing
              : html`
                  ${this.item.type !== 'Arbeidsstasjon'
                    ? nothing
                    : html`<d-view-checkpoint
                        ?checked="${this.item.displayPositionSecure}"
                        field="localUnits_displayPositionSecure"
                      >
                      </d-view-checkpoint>`}
                `}
          </d-section>`}

      <d-expansion ?opened=${this.item.hasMedicalData}>
        ${this.item.type !== 'mobile'
          ? nothing
          : html`<d-section>
              <d-view-checklist
                .checked="${this.item.mobileMedicalDataRequirements}"
                entity="localUnits"
                field="localUnits_mobileMedicalDataRequirements"
                .options="${this.item.mobileMedicalDataRequirementsOptions}"
              >
              </d-view-checklist>
            </d-section>`}
      </d-expansion>

      ${!this.item.notes
        ? nothing
        : html`<d-section>
            <d-view-html field="localUnits_notes" value="${this.item.notes}"> </d-view-html>
          </d-section>`}
      <d-list-section-attachment
        .attachments="${this.item.attachments}"
        class="computers"
        label=${t('computers.attachments')}
        page-id="20"
        sort="1"
        .writeAccess=${this.writeAccess}
        theme-page
      >
      </d-list-section-attachment>
    </section>`;
  }

  renderUnitEdit(editItem: LocalEditItem) {
    return html`
      <d-section>
        <d-select-dropdown
          theme-page
          field="localUnits_type"
          .options=${this.localUnitTypes}
          .value=${editItem.type}
          @value-changed=${(e: CustomEvent<{ value: string }>) => {
            e.stopPropagation();
            this.editItem = {
              ...editItem,
              type: e.detail.value,
            };
          }}
        >
        </d-select-dropdown>
        <d-edit-text
          theme-page
          field="localUnits_name"
          .value=${editItem.name}
          @value-changed=${(e: CustomEvent<{ value: string }>) => {
            e.stopPropagation();
            this.editItem = {
              ...editItem,
              name: e.detail.value,
            };
          }}
        >
        </d-edit-text>
      </d-section>
      <d-section>
        <d-select-dropdown
          theme-page
          field="localUnits_networkUuid"
          .options=${this._networkConnections(this.item.networks)}
          .value=${editItem.networkUuid}
          @value-changed=${(e: CustomEvent<{ value: string }>) => {
            e.stopPropagation();
            this.editItem = {
              ...editItem,
              networkUuid: e.detail.value,
            };
            this.setOperatorTypeDefaultValues(e.detail.value);
          }}
        >
        </d-select-dropdown>
      </d-section>
      <d-section>
        <d-select-dropdown
          theme-page
          field="localUnits_systemUpdateOperatorType"
          .options=${this._operatorTypes(this.item.networkUuid)}
          .value=${editItem.systemUpdateOperatorType}
          @value-changed=${(e: CustomEvent<{ value: string }>) => {
            e.stopPropagation();
            this.editItem = {
              ...editItem,
              systemUpdateOperatorType: e.detail.value,
            };
          }}
        >
        </d-select-dropdown>
      </d-section>
      <d-expansion ?opened="${this._operatorIsExternal(editItem.systemUpdateOperatorType)}">
        <d-section>
          <d-edit-single-related-item
            field="localUnits_systemUpdateOperator"
            .items=${this.item.partners}
            @new-item=${this.onNewPartner}
            placeholder=${t('computers.placeholder.chooseSupplier')}
            .relatedItem=${editItem.systemUpdateOperator}
            @related-item-changed=${(e: CustomEvent<{ relatedItem: string }>) => {
              e.stopPropagation();
              this.editItem = {
                ...editItem,
                systemUpdateOperator: e.detail.relatedItem,
              };
            }}
          >
          </d-edit-single-related-item>
        </d-section>
      </d-expansion>
      <d-section>
        <d-select-dropdown
          theme-page
          field="localUnits_antiVirusOperatorType"
          .options=${this._operatorTypes(this.item.networkUuid)}
          .value=${editItem.antiVirusOperatorType}
          @value-changed=${(e: CustomEvent<{ value: string }>) => {
            e.stopPropagation();
            this.editItem = {
              ...editItem,
              antiVirusOperatorType: e.detail.value,
            };
          }}
        >
        </d-select-dropdown>
      </d-section>
      <d-expansion ?opened=${this._operatorIsExternal(editItem.antiVirusOperatorType)}>
        <d-section>
          <d-edit-single-related-item
            field="localUnits_antiVirusOperator"
            .items=${this.item.partners}
            @new-item=${this.onNewPartner}
            placeholder=${t('computers.placeholder.chooseSupplier')}
            .relatedItem=${editItem.antiVirusOperator}
            @related-item-changed=${(e: CustomEvent<{ relatedItem: string }>) => {
              e.stopPropagation();
              this.editItem = {
                ...editItem,
                antiVirusOperator: e.detail.relatedItem,
              };
            }}
          >
          </d-edit-single-related-item>
        </d-section>
      </d-expansion>
      <d-section>
        <d-select-checkbox
          theme-page
          deselectable
          vertical
          wrap-label
          field="localUnits_antiVirusRequirements"
          option-direction="vertical"
          .options=${this.item.antiVirusRequirementsOptions}
          .value=${editItem.antiVirusRequirements}
          @value-changed=${(e: CustomEvent<{ value: string[] }>) => {
            e.stopPropagation();
            this.editItem = {
              ...editItem,
              antiVirusRequirements: e.detail.value,
            };
          }}
        >
        </d-select-checkbox>
      </d-section>
      <d-expansion ?opened=${editItem.type !== 'mobile'}>
        <d-section>
          <d-edit-text
            theme-page
            field="localUnits_location"
            placeholder=${t('computers.placeholder.describeMachineLocation')}
            value="${editItem.location}"
            @value-changed=${(e: CustomEvent<{ value: string }>) => {
              e.stopPropagation();
              this.editItem = {
                ...editItem,
                location: e.detail.value,
              };
            }}
          >
          </d-edit-text>
        </d-section>
      </d-expansion>

      <d-expansion ?opened=${this.item.hasMedicalData}>
        <d-section>
          <d-checkbox
            theme-page
            wrap-label
            ?checked="${editItem.restrictedPhysicalAccess}"
            option-field="localUnits_restrictedPhysicalAccess"
            @checked-changed=${(e: CustomEvent<{ checked: boolean }>) => {
              e.stopPropagation();
              this.editItem = {
                ...editItem,
                restrictedPhysicalAccess: e.detail.checked,
              };
            }}
          >
          </d-checkbox>
        </d-section>

        <d-expansion ?opened=${editItem.type !== 'mobile'}>
          <d-section>
            <d-checkbox
              theme-page
              wrap-label
              ?checked=${editItem.elevated}
              option-field="localUnits_elevated"
              @checked-changed=${(e: CustomEvent<{ checked: boolean }>) => {
                e.stopPropagation();
                this.editItem = {
                  ...editItem,
                  elevated: e.detail.checked,
                };
              }}
            >
            </d-checkbox>
          </d-section>
          <d-section>
            <d-checkbox
              theme-page
              wrap-label
              ?checked="${editItem.locked}"
              option-field="localUnits_locked"
              @checked-changed=${(e: CustomEvent<{ checked: boolean }>) => {
                e.stopPropagation();
                this.editItem = {
                  ...editItem,
                  locked: e.detail.checked,
                };
              }}
            >
            </d-checkbox>
          </d-section>
        </d-expansion>

        <d-expansion ?opened="${editItem.type === 'mobile'}">
          <d-section>
            <d-select-checkbox
              theme-page
              wrap-label
              deselectable
              field="localUnits_mobileMedicalDataRequirements"
              .options=${this.item.mobileMedicalDataRequirementsOptions}
              .value=${editItem.mobileMedicalDataRequirements}
              vertical
              @value-changed=${(e: CustomEvent<{ value: string[] }>) => {
                e.stopPropagation();
                this.editItem = {
                  ...editItem,
                  mobileMedicalDataRequirements: e.detail.value,
                };
              }}
            >
            </d-select-checkbox>
          </d-section>
        </d-expansion>
      </d-expansion>

      <d-expansion ?opened="${editItem.type === 'Arbeidsstasjon'}">
        <d-section>
          <d-checkbox
            theme-page
            ?checked=${editItem.displayPositionSecure}
            option-field="localUnits_displayPositionSecure"
            @checked-changed=${(e: CustomEvent<{ checked: boolean }>) => {
              e.stopPropagation();
              this.editItem = {
                ...editItem,
                displayPositionSecure: e.detail.checked,
              };
            }}
          >
          </d-checkbox>
        </d-section>
      </d-expansion>
      <d-edit-html
        theme-page
        map-element
        field="localUnits_notes"
        .value=${editItem.notes}
        .contentStickyTop=${this.contentStickyTop}
        @value-changed=${(e: CustomEvent<{ value: string }>) => {
          e.stopPropagation();
          this.editItem = {
            ...editItem,
            notes: e.detail.value,
          };
        }}
      >
      </d-edit-html>
    `;
  }

  initializeEditItem(): LocalEditItem {
    return {
      name: this.item.name,
      systemUpdateOperatorType: this.item.systemUpdateOperatorType,
      systemUpdateOperator: this.item.systemUpdateOperator,
      antiVirusOperator: this.item.antiVirusOperator,
      antiVirusOperatorType: this.item.antiVirusOperatorType,
      locked: this.item.locked,
      antiVirusRequirements: this.item.antiVirusRequirements,
      location: this.item.location,
      elevated: this.item.elevated,
      restrictedPhysicalAccess: this.item.restrictedPhysicalAccess,
      networkUuid: this.item.networkUuid,
      mobileMedicalDataRequirements: this.item.mobileMedicalDataRequirements,
      notes: this.item.notes,
      displayPositionSecure: this.item.displayPositionSecure,
      type: this.item.type,
    };
  }

  protected isDeleteDisabled(): boolean {
    return !this.item.deletable;
  }

  protected typeClasses(): string {
    if (this.item.type === 'Server') {
      return ' server';
    }
    if (this.item.type === 'Arbeidsstasjon') {
      return ' computer';
    }
    if (this.item.type === 'mobile') {
      return ' mobile';
    } else {
      return ' computer';
    }
  }

  private _networkConnections(networks: { name: string; uuid: string }[]): SelectDropdownOption[] {
    const list = [
      { value: '', text: t('computers.placeholder.chooseNetworkConnection') },
      { value: 'NONE', text: t('computers.options.noNetworkConnection') },
    ];
    let unregisteredLocalNetworkTerm = t('computers.localNetwork');
    if (networks.length > 0) {
      unregisteredLocalNetworkTerm = t('computers.otherLocalNetwork');
      for (const element of networks) {
        let value = t('computers.localNetworkWithoutName');
        if (element.name !== '') {
          value = t('computers.localNetworkNamed', { name: element.name });
        }
        list.push({ value: element.uuid, text: value });
      }
    }
    list.push({ value: 'OTHER', text: unregisteredLocalNetworkTerm });
    return list;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'd-computer-unit-local': DComputerUnitLocal;
  }
}
